import { useAuth } from '@/src/context/AuthContext';
import { useTheme } from '@/src/context/ThemeContext';
import {
  followUserUseCase,
  getFollowingUseCase,
  unfollowUserUseCase,
} from '@/src/core/dependencies';
import { FollowWithUser } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { FollowingScreen } from '@/src/presentation/screens/FollowingScreen';
import { useFocusEffect } from '@react-navigation/native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useCallback, useState } from 'react';
import { platformAlert } from '@/src/core/utils/platformAlert';
import { logger } from '@/src/core/utils';

const PAGE_SIZE = 20;

export default function FollowingContainer() {
  const router = useRouter();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { userId } = useLocalSearchParams<{ userId: string }>();
  const { user } = useAuth();
  const [following, setFollowing] = useState<FollowWithUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [followingIds, setFollowingIds] = useState<Set<string>>(new Set());

  useFocusEffect(
    useCallback(() => {
      loadFollowing();
    }, [userId])
  );

  const loadFollowing = async () => {
    if (!userId) return;

    try {
      setLoading(true);
      setPage(0);
      setHasMore(true);
      const data = await getFollowingUseCase.execute(userId, PAGE_SIZE, 0);
      setFollowing(data);
      setHasMore(data.length === PAGE_SIZE);
      setFollowingIds(new Set(data.map((f) => f.followingId)));
    } catch (error) {
      logger.error('Following', 'Failed to load following', { error });
      platformAlert(t('common.error'), t('errors.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const loadMoreFollowing = async () => {
    if (loadingMore || !hasMore || !userId) return;

    try {
      setLoadingMore(true);
      const nextPage = page + 1;
      const data = await getFollowingUseCase.execute(userId, PAGE_SIZE, nextPage * PAGE_SIZE);

      if (data.length > 0) {
        setFollowing((prev) => [...prev, ...data]);
        setPage(nextPage);
        setHasMore(data.length === PAGE_SIZE);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      logger.error('Following', 'Failed to load more following', { error });
    } finally {
      setLoadingMore(false);
    }
  };

  const handleFollowToggle = async (targetUserId: string) => {
    if (!user) return;

    try {
      const isFollowing = followingIds.has(targetUserId);

      if (isFollowing) {
        await unfollowUserUseCase.execute(user.id, targetUserId);
        setFollowingIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(targetUserId);
          return newSet;
        });
      } else {
        await followUserUseCase.execute(user.id, targetUserId);
        setFollowingIds((prev) => new Set(prev).add(targetUserId));
      }
    } catch (error) {
      logger.error('Following', 'Failed to toggle follow', { error });
      platformAlert(t('common.error'), error instanceof Error ? error.message : t('errors.failedToLoad'));
    }
  };

  const handleUserPress = (targetUserId: string) => {
    router.push(`/profile/${targetUserId}`);
  };

  return (
    <FollowingScreen
      following={following}
      loading={loading}
      loadingMore={loadingMore}
      currentUserId={user?.id}
      followingIds={followingIds}
      onFollowToggle={handleFollowToggle}
      onUserPress={handleUserPress}
      onRefresh={loadFollowing}
      onLoadMore={loadMoreFollowing}
      colors={colors}
    />
  );
}
